// eslint-disable-next-line no-restricted-imports
import axios from 'axios'
import Swiper from 'swiper'
import SwiperCore, { Lazy, Navigation } from 'swiper/core'
import { addProduct } from '../../../scripts/helpers/cart'
import { getLocalURL } from '../../../scripts/helpers/globale'
import { throttleTapMobile } from '../../../scripts/helpers/quick-add'
SwiperCore.use([Navigation, Lazy])

export default class ProductCard {
  constructor(component) {
    this.dom = {
      $component: component,
      $body: document.querySelector('body'),
      $swiperContainer: component.querySelector('.js-productCardContainer'),
      $swiperPrev: component.querySelector('.js-productCardSliderPrev'),
      $swiperNext: component.querySelector('.js-productCardSliderNext'),
      $wishListButton: component.querySelector('.js-iWishAdd'),
      $quickAddButtonWraps: component.querySelectorAll('.js-quickAddBtnWrap'),
      $quickAddButtons: component.querySelectorAll('.js-quickAdd'),
      $quickAddSingleButton: component.querySelector('.js-quickAddSingle'),
      $quickAddSizeButtons: component.querySelectorAll('.js-quickAddSize'),
      $quickAddMobileButtons: component.querySelectorAll('.js-product-card-quick-add-mobile'),
      $sizeOptionBtnContainer: component.querySelector('.js-mulitSizeOptions'),
      $swatchContainer: component.querySelector('.js-swatchesDesktop'),
      $mobileSwatchContainer: component.querySelector('.js-swatchesMobile'),
      $links: component.querySelectorAll('.js-productCardLink'),
      $imageCover: component.querySelector('.js-productCardImageCover'),
      $skeletonLoad: component.querySelector('.js-productCardSkeletonLoad'),
    }
    this.state = {
      firstImageLoadedClass: 'firstImageLoaded',
      productInWishlist: false,
      inWishlistClass: 'inWishlist',
      manyOptionsClass: 'hasManyOptions',
      observer: null,
      swatchesLoaded: false,
      swatches: [],
      activeSwatch: null,
      swatchDisplayLimit: 4,
      activeSwatchClass: 'activeSwatch',
      swiper: null,
      firstImageLoaded: false,
      revealClass: 'reveal',
      removeClass: 'remove',
      animationTiming: 500,
      hideQuickAddClass: 'hidden',
      showSingleAdd: false,
      showMultiSize: false,
    }
    this.wishListClickHandler = this.wishListClick.bind(this)
    this.quickAddHandler = this.quickAdd.bind(this)

    this.mount()
    this.bindToWindow()

    this.init()
  }

  mount() {
    const { dom, state } = this
    dom.$quickAddButtons.forEach(($btn) => $btn.addEventListener('click', this.quickAddHandler))

    if (dom.$swiperContainer) {
      this.wishListHandler()
      this.initSlider()
      this.checkWishlist()
    }

    if (dom.$quickAddSizeButtons.length >= 9) {
      dom.$sizeOptionBtnContainer.classList.add(state.manyOptionsClass)
    }

    this.updateQuickAddMobile()
  }

  init() {
    this.observe()
  }

  wishListHandler() {
    const { dom } = this
    if (dom.$wishListButton) {
      dom.$wishListButton.addEventListener('click', this.wishListClickHandler)
    }
  }

  observe() {
    const { dom, state } = this

    state.observer = new IntersectionObserver(this.onIntersection.bind(this), {
      rootMargin: '250px',
    })
    state.observer.observe(dom.$component)
  }

  onIntersection(entries) {
    const { state: { observer } } = this
    const entry = entries[0]

    if (entry.isIntersecting) {
      this.loadSwatches()
      observer.unobserve(entry.target)
    }
  }

  async loadSwatches() {
    const { dom, state } = this

    if (!dom.$swatchContainer) {
      state.swatchesLoaded = true
      this.removeSkeletonLoad()
      return
    }

    try {
      const { group, color } = dom.$swatchContainer.dataset
      const url = getLocalURL(`/collections/all/${group}?view=swatch-data`)
      const { data: { products } } = await axios.get(url)
      state.swatches = products
      state.activeSwatch = color
      this.mountSwatches()
      state.swatchesLoaded = true
      this.removeSkeletonLoad()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Product Card: Error loading swatches', dom.$component.dataset.productId, err)
    }
  }

  mountSwatches() {
    const { state } = this
    const activeSwatch = state.swatches?.find(({ color }) => color === state.activeSwatch)

    if (!activeSwatch) {
      return
    }

    this.createSwatch(activeSwatch, false)
    this.createSwatch(activeSwatch, true)

    let count = 1
    let extraSwatches = false

    state.swatches.forEach((swatch) => {
      if (!swatch.rendered) {
        this.createSwatch(swatch, false)
      }

      if (!swatch.renderedMobile) {
        if (count >= state.swatchDisplayLimit) {
          extraSwatches = true
        } else {
          this.createSwatch(swatch, true)
          count += 1
        }
      }
    })

    if (extraSwatches) {
      this.addExtraSwatchesLabel()
    }
  }

  addExtraSwatchesLabel() {
    const { dom } = this
    const extraSwatchLabel = document.createElement('span')
    extraSwatchLabel.classList.add('ProductCardSwatches__moreColorsIcon')
    extraSwatchLabel.innerHTML = `
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.50488 0V3.7666H8.18945V4.56641H4.50488V8.34668H3.69824V4.56641H0V3.7666H3.69824V0H4.50488Z" fill="black"/>
    </svg>
    `
    dom.$mobileSwatchContainer.append(extraSwatchLabel)
  }

  createSwatch(swatch, onMobile) {
    const { dom, state } = this
    const { template, collections } = window.theme.strings
    const swatchModifiers = swatch.color === state.activeSwatch ? 'activeSwatch' : ''
    const button = document.createElement('button')
    button.ariaLabel = `View ${swatch.color} product images`
    button.title = `${swatch.title} - ${swatch.color}`
    button.dataset.colorName = swatch.color
    button.dataset.productId = swatch.productId
    button.dataset.url = swatch.url
    button.classList.add('js-swatchButton')

    if (template.name === 'collection') {
      const productUrl = swatch.url.split('/products/')[1]
      button.dataset.url = getLocalURL(`/collections/${collections.handle}/products/${productUrl}`)
    }

    button.innerHTML = `
    <span class="Swatch js-Swatch productCard ${swatchModifiers}">
      <span
        class="Swatch__inset productSwatch"
        data-color="${swatch.colorHandle}"
      ></span>
    </span>
    `

    button.addEventListener('click', this.swatchClick.bind(this))
    if (onMobile) {
      dom.$mobileSwatchContainer.append(button)
      swatch.renderedMobile = true
    } else {
      dom.$swatchContainer.append(button)
      swatch.rendered = true
    }
  }

  swatchClick(event) {
    const swatchButton = event.currentTarget

    window.location.href = swatchButton.dataset.url
  }

  updateLinks(swatch) {
    const { dom } = this

    const { collectionHandle } = dom.$component.dataset
    const url = collectionHandle ? `/collections/${collectionHandle + swatch.url}` : swatch.url
    dom.$links.forEach((link) => link.setAttribute('href', url))
  }

  updateNotEligible(swatch) {
    const { dom } = this

    dom.$quickAddButtons.forEach((btn) => {
      if (swatch.notEligible) {
        btn.dataset.notEligible = swatch.notEligibleCopy
      } else {
        btn.removeAttribute('data-not-eligible')
      }
    })
  }

  updateWishlistButton(swatch) {
    const { dom } = this

    dom.$wishListButton.dataset.productId = swatch.productId
    dom.$wishListButton.dataset.currentVariantId = swatch.variants[0].variantId
  }

  updateQuickAdd(swatch) {
    const { dom, state } = this

    dom.$component.dataset.productId = swatch.productId

    this.revealQuickAddButton(swatch)

    if (state.showSingleAdd) {
      dom.$quickAddSingleButton.dataset.variantId = swatch.variants[0].variantId
    } else if (state.showMultiSize) {
      swatch.variants.forEach((variant) => {
        const size = variant.variantOptions.Size
        const available = variant.variantAvailable

        const matchingSizeButton = dom.$component.querySelector(`.js-quickAddSize[data-size-value="${size}"]`)
        matchingSizeButton.dataset.variantId = variant.variantId
        if (available) {
          matchingSizeButton.removeAttribute('disabled')
        } else {
          matchingSizeButton.setAttribute('disabled', '')
        }
      })
    }
  }

  /**
   * Enable mobile quick add variant buttons
   */
  updateQuickAddMobile() {
    const { dom } = this
    if (!dom.$quickAddMobileButtons) {
      return
    }

    throttleTapMobile(dom.$quickAddMobileButtons)
  }

  /**
   * Use the same logic as product-card-quickadd.liquid to determine
   * which button should be rendered.
   * @param {*} swatch
   */
  revealQuickAddButton(swatch) {
    const { state } = this

    if (swatch.available) {
      if (swatch.preOrder) {
        this.revealButtonOfType('preOrder')
      } else if (swatch.onlyDefaultVariant) {
        this.revealButtonOfType('onlyDefaultVariant')
        state.showSingleAdd = true
      } else if (swatch.optionsSize === 1 && swatch.colorOptions) {
        this.revealButtonOfType('onlyColorVariants')
        state.showSingleAdd = true
      } else {
        this.revealButtonOfType('multiSize')
        state.showMultiSize = true
      }
    } else if (swatch.comingSoon || swatch.waitlist) {
      this.revealButtonOfType('waitlist')
    } else {
      this.revealButtonOfType('notAvailable')
    }
  }

  revealButtonOfType(btnType) {
    const { dom, state } = this

    dom.$quickAddButtonWraps.forEach((btnWrap) => {
      if (btnWrap.dataset.buttonType === btnType) {
        btnWrap.classList.remove(state.hideQuickAddClass)
      } else {
        btnWrap.classList.add(state.hideQuickAddClass)
      }
    })
  }

  setActiveSwatch(selectedSwatchButton) {
    const { state } = this
    const currentActiveSwatch = selectedSwatchButton.parentElement.querySelector(`.js-swatchButton[data-color-name="${state.activeSwatch}"]`)

    currentActiveSwatch.querySelector('.js-Swatch').classList.remove(state.activeSwatchClass)
    selectedSwatchButton.querySelector('.js-Swatch').classList.add(state.activeSwatchClass)
    state.activeSwatch = selectedSwatchButton.dataset.colorName
  }

  rebuildSlider(images) {
    const { dom, state } = this
    const imageContainer = document.createElement('div')
    imageContainer.className = 'ProductCardSlider__wrapper swiper-wrapper'

    images.forEach((image) => {
      const slide = this.buildNewSlide(image)
      imageContainer.append(slide)
    })

    state.swiper.destroy()
    dom.$swiperContainer.innerHTML = ''
    dom.$swiperContainer.append(imageContainer)
  }

  buildNewSlide({ src, alt }) {
    const slide = document.createElement('div')
    const image = document.createElement('img')
    slide.className = 'ProductCardSlider__slide swiper-slide'
    image.className = 'ProductCardSlider__image swiper-lazy'
    image.dataset.src = src
    image.alt = alt
    slide.append(image)
    return slide
  }

  initSlider() {
    const { dom, state } = this

    const swiperConfig = {
      loop: true,
      allowTouchMove: false,
      preloadImages: false,
      lazy: {
        loadOnTransitionStart: true,
      },
    }

    if (dom.$swiperNext && dom.$swiperPrev) {
      swiperConfig.navigation = {
        prevEl: dom.$swiperPrev,
        nextEl: dom.$swiperNext,
      }
    }

    state.swiper = new Swiper(dom.$swiperContainer, swiperConfig)

    this.firstImageLoad()
  }

  firstImageLoad() {
    const { dom, state } = this
    state.firstImageLoaded = true
    dom.$component.classList.add(state.firstImageLoadedClass)
    this.removeSkeletonLoad()
  }

  removeSkeletonLoad() {
    const { state } = this

    if (state.firstImageLoaded && state.swatchesLoaded) {
      this.removeImageCover()
      this.removeInfoCover()
    }
  }

  removeImageCover() {
    const { dom, state } = this

    dom.$imageCover.classList.add(state.revealClass)
    setTimeout(() => dom.$imageCover.classList.add(state.removeClass), state.animationTiming)
  }

  removeInfoCover() {
    const { dom, state } = this

    dom.$skeletonLoad.classList.add(state.revealClass)
    setTimeout(() => dom.$skeletonLoad.classList.add(state.removeClass), state.animationTiming)
  }

  addImageCover() {
    const { dom, state } = this

    dom.$imageCover.classList.remove(state.removeClass)
    dom.$imageCover.classList.remove(state.revealClass)
  }

  checkWishlist() {
    const { dom, state } = this

    if (!dom.$wishListButton) {
      return
    }

    const { productId, currentVariantId } = dom.$wishListButton.dataset

    if (window.isInWishlist(productId, currentVariantId)) {
      state.productInWishlist = true
      dom.$wishListButton.classList.add(state.inWishlistClass)
    } else {
      state.productInWishlist = false
      dom.$wishListButton.classList.remove(state.inWishlistClass)
    }
  }

  wishListClick(event) {
    const { dom, state } = this

    if (state.productInWishlist) {
      return
    }

    const {
      productId,
      productTitle,
      currentVariantId,
      customer,
    } = event.currentTarget.dataset

    if (customer) {
      window.iwish_addOnly(productId, currentVariantId, productTitle)
      dom.$wishListButton.classList.add(state.inWishlistClass)
      state.productInWishlist = true
    } else {
      const loginModalEvent = new Event('loginModalToggle')
      document.dispatchEvent(loginModalEvent)
    }
  }

  quickAdd(event) {
    const isUpsell = event.target.closest('.js-cartItemUpsell-item')
    const { dom } = this
    const { productId } = dom.$component.dataset
    const { notEligible, promoBadge, variantId } = event.currentTarget.dataset
    const properties = {}

    if (notEligible) {
      properties['Not-eligible'] = notEligible
    }

    if (promoBadge) {
      // eslint-disable-next-line
      properties._promo_badge = promoBadge
    }

    addProduct(productId, variantId, 1, properties, isUpsell)
  }

  bindToWindow() {
    window.theme = window.theme || {}
    window.theme.ProductCard = this
  }
}
